"use client";

import { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  format,
  addDays,
  addMonths,
  subMonths,
  isBefore,
  isToday,
  startOfDay,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  getDay,
} from "date-fns";
import { CaretLeft, CaretRight, SpinnerGap, CheckCircle, Clock, Timer, Users, X as XIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { useIsMobile } from "@/hooks/use-mobile";
import { TimeGrid } from "@/components/venue-bookings/TimeGrid";
import { getConflictSpaceIds, isRangeAvailable } from "@/lib/booking-conflicts";

import { VENUES } from "@/lib/venues";

const SAST = "Africa/Johannesburg";

// ---------------------------------------------------------------------------
// Schema — time values come from the TimeGrid, not typed inputs
// ---------------------------------------------------------------------------
const schema = z.object({
  guestName:    z.string().min(2,  "Full name is required."),
  guestEmail:   z.string().email("A valid email address is required."),
  guestPhone:   z.string().min(7,  "A valid phone number is required."),
  organization: z.string().optional(),
  purpose:      z.string().min(10, "Please describe the purpose (at least 10 characters)."),
  website:      z.string().max(0, "").optional(),
});

type FormValues = z.infer<typeof schema>;

interface TimeRange {
  start: string; // "HH:mm"
  end:   string; // "HH:mm"
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export function BookingForm() {
  const isMobile = useIsMobile();
  const [venueId, setVenueId]           = useState(VENUES[0]?.id ?? "");
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [sheetOpen, setSheetOpen]       = useState(false);
  const [submitted, setSubmitted]       = useState(false);
  const [referenceId, setReferenceId]   = useState<number | null>(null);

  // Space / addon selection
  const [selectedSpaces, setSelectedSpaces] = useState<string[]>([]);
  const [selectedAddons,  setSelectedAddons]  = useState<string[]>([]);
  const [spaceError, setSpaceError]           = useState<string | null>(null);

  // Per-space blocked time ranges (from availability API for the selected day)
  const [spaceRanges, setSpaceRanges] = useState<Record<string, TimeRange[]>>({});
  const [flatRanges,  setFlatRanges]  = useState<TimeRange[]>([]);
  const [loadingRanges, setLoadingRanges] = useState(false);

  // Time selection (HH:mm)
  const [selectedStart, setSelectedStart] = useState<string | null>(null);
  const [selectedEnd,   setSelectedEnd]   = useState<string | null>(null);

  const venue = VENUES.find((v) => v.id === venueId);

  // ── month-level fetch (no longer greys days — just kept for future use) ──
  const fetchMonthBusy = useCallback(async () => {
    // With time-based booking, the calendar always shows all future days as
    // available. We keep this hook so VenueCard availability dots still work.
  }, [venueId, currentMonth]);

  useEffect(() => { void fetchMonthBusy(); }, [fetchMonthBusy]);

  // Reset everything when venue changes
  useEffect(() => {
    setSelectedSpaces([]);
    setSelectedAddons([]);
    setSpaceError(null);
    resetTimeSelection();
    setSpaceRanges({});
    setFlatRanges([]);
  }, [venueId]);

  // Reset time whenever space selection changes (different spaces = different blocked ranges)
  useEffect(() => {
    resetTimeSelection();
  }, [selectedSpaces]);

  function resetTimeSelection() {
    setSelectedStart(null);
    setSelectedEnd(null);
  }

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      guestName: "", guestEmail: "", guestPhone: "",
      organization: "", purpose: "", website: "",
    },
  });

  const { isSubmitting } = form.formState;

  // ── Compute effective blocked ranges for the time grid ───────────────────
  // Union of all ranges for the currently-selected spaces (+ their conflicts)
  const effectiveBlockedRanges: TimeRange[] = (() => {
    if (!venue?.spaces) return flatRanges;
    if (selectedSpaces.length === 0) {
      // No spaces chosen yet — show combined blocked view for ALL spaces
      const all: TimeRange[] = [];
      for (const ranges of Object.values(spaceRanges)) all.push(...ranges);
      return all;
    }
    const conflictIds = getConflictSpaceIds(venue, selectedSpaces);
    const merged: TimeRange[] = [];
    for (const id of conflictIds) {
      if (spaceRanges[id]) merged.push(...spaceRanges[id]);
    }
    return merged;
  })();

  function handleDayClick(day: Date) {
    setSelectedDate(day);
    setSubmitted(false);
    setSpaceError(null);
    resetTimeSelection();
    setSpaceRanges({});
    setFlatRanges([]);
    form.reset();
    setSheetOpen(true);

    // Fetch blocked time ranges for this day
    // Build a SAST-local day window so availability is stable across browser timezones.
    const dateStr = format(day, "yyyy-MM-dd");
    const nextDateStr = format(addDays(day, 1), "yyyy-MM-dd");
    const from = `${dateStr}T00:00:00+02:00`;
    const to   = `${nextDateStr}T00:00:00+02:00`;
    setLoadingRanges(true);
    fetch(
      `/api/venue-bookings/availability?venueId=${venueId}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      { cache: "no-store" }
    )
      .then((r) => r.json() as Promise<{
        spaceRanges?: Record<string, TimeRange[]>;
        ranges?: TimeRange[];
      }>)
      .then((d) => {
        setSpaceRanges(d.spaceRanges ?? {});
        setFlatRanges(d.ranges ?? []);
      })
      .catch(() => { /* silent */ })
      .finally(() => setLoadingRanges(false));
  }

  async function onSubmit(values: FormValues) {
    if (!selectedDate) return;

    // Validate time selection
    if (!selectedStart || !selectedEnd) {
      toast.error("Please select a start and end time in the time grid.");
      return;
    }

    // Client-side range check against effective blocked ranges
    if (!isRangeAvailable(selectedStart, selectedEnd, effectiveBlockedRanges)) {
      toast.error("The selected time range overlaps with an existing booking or grace period. Please choose a different time.");
      return;
    }

    // Space selection required if venue has spaces
    if (venue?.spaces && venue.spaces.length > 0 && selectedSpaces.length === 0) {
      setSpaceError("Please select at least one space.");
      return;
    }
    setSpaceError(null);

    const dateStr = format(selectedDate, "yyyy-MM-dd");
    // +02:00 = Africa/Johannesburg (SAST, no DST)
    const startTime = `${dateStr}T${selectedStart}:00+02:00`;
    const endTime   = `${dateStr}T${selectedEnd}:00+02:00`;

    const res = await fetch("/api/venue-bookings", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        guestName:      values.guestName,
        guestEmail:     values.guestEmail,
        guestPhone:     values.guestPhone,
        organization:   values.organization,
        venueId,
        startTime,
        endTime,
        purpose:        values.purpose,
        website:        values.website,
        spaceSelection: selectedSpaces,
        addons:         selectedAddons,
      }),
    });

    const data = (await res.json()) as { success?: boolean; referenceId?: number; error?: string };
    if (!res.ok || !data.success) {
      toast.error(data.error ?? "Something went wrong. Please try again.");
      return;
    }
    setReferenceId(data.referenceId ?? null);
    setSubmitted(true);
  }

  // ── Calendar grid helpers ──────────────────────────────────────────────
  const monthStart = startOfMonth(currentMonth);
  const monthEnd   = endOfMonth(currentMonth);
  const days       = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const today      = startOfDay(new Date());
  const minDate    = addDays(today, 1);
  const startDow   = (getDay(monthStart) + 6) % 7;
  const blanks     = Array.from({ length: startDow });

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">

      {/* ── Controls bar ─────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center sm:gap-3 flex-1 min-w-0">
          <select
            id="venue-select"
            value={venueId}
            onChange={(e) => setVenueId(e.target.value)}
            className="h-11 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm font-semibold shadow-sm focus:outline-none focus:ring-2 focus:ring-school-navy/20 transition sm:h-10 sm:w-auto sm:min-w-52"
          >
            <optgroup label="Indoor Venues">
              {VENUES.filter((v) => v.category === "indoor").map((v) => (
                <option key={v.id} value={v.id}>{v.name}</option>
              ))}
            </optgroup>
            <optgroup label="Outdoor Venues">
              {VENUES.filter((v) => v.category === "outdoor").map((v) => (
                <option key={v.id} value={v.id}>{v.name}</option>
              ))}
            </optgroup>
          </select>
          {venue && (
            <div className="flex flex-wrap gap-1.5">
              <span className="inline-flex items-center gap-1 rounded-full bg-school-navy/8 px-2.5 py-0.5 text-[11px] font-medium text-school-navy">
                <Clock size={12} weight="bold" />
                {venue.operatingHours.start}–{venue.operatingHours.end}
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-school-gold/15 px-2.5 py-0.5 text-[11px] font-medium text-amber-800">
                <Timer size={12} weight="bold" />
                Max {venue.maxBookingHours}h
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-0.5 text-[11px] font-medium text-blue-700">
                <Users size={12} weight="bold" />
                {venue.capacity.toLocaleString()}
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 sm:justify-end sm:shrink-0">
          <Button variant="outline" size="icon" type="button"
            className="h-9 w-9 rounded-lg"
            onClick={() => setCurrentMonth((m) => subMonths(m, 1))}>
            <CaretLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm font-bold text-school-navy tabular-nums w-36 text-center">
            {format(currentMonth, "MMMM yyyy")}
          </span>
          <Button variant="outline" size="icon" type="button"
            className="h-9 w-9 rounded-lg"
            onClick={() => setCurrentMonth((m) => addMonths(m, 1))}>
            <CaretRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* ── Calendar grid ─────────────────────────────────────────────────── */}
      <div className="rounded-xl overflow-hidden shadow-sm select-none border border-gray-200">
        {/* Day-of-week header */}
        <div className="grid grid-cols-7 bg-school-navy text-white text-[11px] font-semibold text-center">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
            <div key={d} className="py-2.5 tracking-wide">{d}</div>
          ))}
        </div>
        {/* Day cells */}
        <div className="grid grid-cols-7 bg-gray-50">
          {blanks.map((_, i) => <div key={`b${i}`} className="h-14 bg-gray-50/60 border-b border-r border-gray-100" />)}
          {days.map((day) => {
            const key        = day.toLocaleDateString("en-CA", { timeZone: SAST });
            const isPast     = isBefore(day, minDate);
            const isT        = isToday(day);
            const isDisabled = isPast;
            return (
              <button
                key={key}
                type="button"
                disabled={isDisabled}
                onClick={() => !isDisabled && handleDayClick(day)}
                className={[
                  "h-14 flex items-center justify-center border-b border-r border-gray-100 transition-all w-full",
                  isPast
                    ? "bg-gray-50/80 cursor-not-allowed"
                    : "bg-white hover:bg-school-ice active:bg-school-sky-light cursor-pointer",
                  isT && !isDisabled ? "ring-2 ring-inset ring-school-gold" : "",
                ].join(" ")}
              >
                <span className={[
                  "text-sm font-semibold w-8 h-8 flex items-center justify-center rounded-full transition-colors",
                  isT && !isDisabled ? "bg-school-navy text-white"
                    : isDisabled     ? "text-gray-300"
                    : "text-gray-700",
                ].join(" ")}>
                  {format(day, "d")}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-[11px] text-gray-400 px-1">
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-gray-50 border border-gray-200" />
          Past
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-white border border-gray-200" />
          Available
        </span>
      </div>

      {/* ── Booking form (Sheet on mobile, Dialog on tablet+) ─────────────── */}
      {(() => {
        const formContent = submitted ? (
          <div className="flex flex-col items-center justify-center h-full min-h-100 gap-5 px-8 text-center">
            <div className="w-14 h-14 rounded-2xl bg-green-100 flex items-center justify-center shadow-sm">
              <CheckCircle className="w-7 h-7 text-green-600" weight="fill" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Request Submitted!</h3>
              {referenceId && (
                <p className="text-sm text-gray-400 mt-1">Ref <strong className="text-gray-700">#{referenceId}</strong></p>
              )}
            </div>
            <p className="text-sm text-gray-500 max-w-xs leading-relaxed">
              We&apos;ve sent you a confirmation email. Our team will review your request within 2 business days.
            </p>
            <div className="flex gap-2 mt-1">
              <Button variant="outline" className="rounded-xl" onClick={() => setSheetOpen(false)}>Close</Button>
              <Button className="bg-school-navy hover:bg-school-navy-dark text-white rounded-xl"
                onClick={() => { setSubmitted(false); form.reset(); resetTimeSelection(); }}>
                Book Another
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="bg-school-navy px-5 pt-6 pb-4">
              {isMobile ? (
                <SheetHeader>
                  <SheetTitle className="text-white text-lg font-bold text-left">
                    {venue?.name}
                  </SheetTitle>
                  {selectedDate && (
                    <p className="text-school-gold text-sm text-left">
                      {format(selectedDate, "EEEE, d MMMM yyyy")}
                    </p>
                  )}
                </SheetHeader>
              ) : (
                <DialogHeader className="relative">
                  <DialogTitle className="text-white text-lg font-bold text-left">
                    {venue?.name}
                  </DialogTitle>
                  {selectedDate && (
                    <p className="text-school-gold text-sm text-left">
                      {format(selectedDate, "EEEE, d MMMM yyyy")}
                    </p>
                  )}
                  <DialogClose className="absolute right-0 top-0 rounded-sm opacity-80 transition-opacity hover:opacity-100 focus:outline-hidden w-10 h-10 flex items-center justify-center">
                    <XIcon className="h-6 w-6 text-white" />
                    <span className="sr-only">Close</span>
                  </DialogClose>
                </DialogHeader>
              )}
            </div>

            <form onSubmit={form.handleSubmit(onSubmit)} className="px-4 py-4">
              {/* Honeypot */}
              <div className="absolute opacity-0 pointer-events-none h-0 overflow-hidden" aria-hidden>
                <input type="text" tabIndex={-1} autoComplete="off" {...form.register("website")} />
              </div>

              <div className={isMobile ? "space-y-3" : "grid grid-cols-2 gap-4"}>
                {/* Left column: spaces + time */}
                <div className="space-y-3">
                  {/* Space selection */}
                  {venue?.spaces && venue.spaces.length > 0 && (() => {
                    // Group spaces by their sub-label (e.g. "Netball / Outdoor", "Tennis / Outdoor")
                    // Ungrouped spaces (no parenthetical) go into a separate "Other" group
                    const groups: { label: string; spaces: typeof venue.spaces }[] = [];
                    const groupMap = new Map<string, typeof venue.spaces>();

                    for (const space of venue.spaces!) {
                      const match = space.label.match(/^([^(]+?)\s*\((.+)\)$/);
                      const groupKey = match ? match[2].trim() : "";
                      if (!groupMap.has(groupKey)) {
                        groupMap.set(groupKey, []);
                      }
                      groupMap.get(groupKey)!.push(space);
                    }

                    for (const [key, spaces] of groupMap) {
                      groups.push({ label: key, spaces });
                    }

                    return (
                      <div className="bg-white rounded-xl p-4 shadow-sm">
                        <p className="text-sm font-bold text-school-navy mb-3">Choose your space</p>
                        <div className="space-y-4">
                          {groups.map((group) => (
                            <div key={group.label || "other"}>
                              {group.label && groups.length > 1 && (
                                <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-400 mb-2">
                                  {group.label}
                                </p>
                              )}
                              <div className="flex flex-wrap gap-2">
                                {group.spaces.map((space) => {
                                  const on = selectedSpaces.includes(space.id);
                                  const match = space.label.match(/^([^(]+?)\s*\((.+)\)$/);
                                  const mainLabel = match ? match[1].trim() : space.label;
                                  const subLabel = match ? match[2] : null;
                                  return (
                                    <button
                                      key={space.id}
                                      type="button"
                                      onClick={() =>
                                        setSelectedSpaces((prev) =>
                                          prev.includes(space.id)
                                            ? prev.filter((s) => s !== space.id)
                                            : [...prev, space.id]
                                        )
                                      }
                                      className={[
                                        "rounded-xl border px-4 py-2.5 text-left transition-all duration-150",
                                        on
                                          ? "bg-school-gold text-school-navy border-school-gold shadow-md"
                                          : "bg-school-gold/15 text-school-navy border-school-gold/30 hover:bg-school-gold/30 active:scale-[0.97]",
                                      ].join(" ")}
                                    >
                                      <span className="block text-sm font-bold leading-tight">{mainLabel}</span>
                                      {subLabel && (
                                        <span className={["block text-[11px] leading-tight mt-0.5", on ? "text-school-navy/70" : "text-school-navy/50"].join(" ")}>
                                          {subLabel}
                                        </span>
                                      )}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                        {spaceError && <p className="text-xs text-red-500 mt-2">{spaceError}</p>}
                      </div>
                    );
                  })()}

                  {/* Time grid */}
                  <div className="bg-white rounded-xl p-4 shadow-sm">
                    <p className="text-sm font-bold text-school-navy mb-3">Pick your time</p>
                    {loadingRanges ? (
                      <div className="flex items-center gap-2 text-sm text-gray-400 py-8 justify-center">
                        <SpinnerGap className="h-4 w-4 animate-spin" />
                        Checking availability&hellip;
                      </div>
                    ) : venue ? (
                      <TimeGrid
                        operatingStart={venue.operatingHours.start}
                        operatingEnd={venue.operatingHours.end}
                        blockedRanges={effectiveBlockedRanges}
                        selectedStart={selectedStart}
                        selectedEnd={selectedEnd}
                        onSelect={(s, e) => { setSelectedStart(s); setSelectedEnd(e); }}
                      />
                    ) : null}
                  </div>
                </div>

                {/* Right column: addons + contact + submit */}
                <div className="space-y-3">
                  {/* Addons */}
                  {venue?.addons && venue.addons.length > 0 && (
                    <div className="bg-white rounded-xl p-4 shadow-sm">
                      <p className="text-sm font-bold text-school-navy mb-1">Extras</p>
                      {venue.addons.some((a) => a.enquiryOnly) && (
                        <p className="text-xs text-gray-400 mb-3">Subject to availability.</p>
                      )}
                      <div className="flex flex-wrap gap-2">
                        {venue.addons.map((addon) => {
                          const on = selectedAddons.includes(addon.id);
                          return (
                            <button
                              key={addon.id}
                              type="button"
                              onClick={() =>
                                setSelectedAddons((prev) =>
                                  prev.includes(addon.id)
                                    ? prev.filter((a) => a !== addon.id)
                                    : [...prev, addon.id]
                                )
                              }
                              className={[
                                "rounded-xl border px-4 py-2.5 text-left transition-all duration-150",
                                on
                                  ? "bg-school-gold text-school-navy border-school-gold shadow-md"
                                  : "bg-school-gold/15 text-school-navy border-school-gold/30 hover:bg-school-gold/30 active:scale-[0.97]",
                              ].join(" ")}
                            >
                              <span className="block text-sm font-bold leading-tight">{addon.label}</span>
                              {addon.enquiryOnly && (
                                <span className={["block text-[11px] leading-tight mt-0.5", on ? "text-school-navy/70" : "text-school-navy/50"].join(" ")}>
                                  enquiry only
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Contact details */}
                  <div className="bg-white rounded-xl p-4 shadow-sm space-y-3">
                    <p className="text-sm font-bold text-school-navy">Your details</p>

                    <div>
                      <Label className="text-xs font-semibold text-gray-500">Full Name *</Label>
                      <Input className="mt-1 h-11 rounded-xl bg-gray-50 border-gray-200 focus-visible:ring-school-navy/30 focus-visible:border-school-navy/40" placeholder="Jane Smith" {...form.register("guestName")} />
                      {form.formState.errors.guestName && (
                        <p className="text-xs text-red-500 mt-1">{form.formState.errors.guestName.message}</p>
                      )}
                    </div>

                    <div>
                      <Label className="text-xs font-semibold text-gray-500">Email *</Label>
                      <Input className="mt-1 h-11 rounded-xl bg-gray-50 border-gray-200 focus-visible:ring-school-navy/30 focus-visible:border-school-navy/40" type="email" placeholder="jane@email.com" {...form.register("guestEmail")} />
                      {form.formState.errors.guestEmail && (
                        <p className="text-xs text-red-500 mt-1">{form.formState.errors.guestEmail.message}</p>
                      )}
                    </div>

                    <div>
                      <Label className="text-xs font-semibold text-gray-500">Phone *</Label>
                      <Input className="mt-1 h-11 rounded-xl bg-gray-50 border-gray-200 focus-visible:ring-school-navy/30 focus-visible:border-school-navy/40" type="tel" placeholder="0xx xxx xxxx" {...form.register("guestPhone")} />
                      {form.formState.errors.guestPhone && (
                        <p className="text-xs text-red-500 mt-1">{form.formState.errors.guestPhone.message}</p>
                      )}
                    </div>

                    <div>
                      <Label className="text-xs font-semibold text-gray-500">
                        Organisation <span className="font-normal text-gray-400">(optional)</span>
                      </Label>
                      <Input className="mt-1 h-11 rounded-xl bg-gray-50 border-gray-200 focus-visible:ring-school-navy/30 focus-visible:border-school-navy/40" placeholder="School, club, or company" {...form.register("organization")} />
                    </div>

                    <div>
                      <Label className="text-xs font-semibold text-gray-500">Purpose of Booking *</Label>
                      <Textarea
                        className="mt-1 rounded-xl bg-gray-50 border-gray-200 focus-visible:ring-school-navy/30 focus-visible:border-school-navy/40 resize-none"
                        rows={2}
                        placeholder="What's the booking for?"
                        {...form.register("purpose")}
                      />
                      {form.formState.errors.purpose && (
                        <p className="text-xs text-red-500 mt-1">{form.formState.errors.purpose.message}</p>
                      )}
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-school-navy hover:bg-school-navy-dark text-white h-12 rounded-xl font-bold text-[15px] shadow-lg transition-all active:scale-[0.98]"
                  >
                    {isSubmitting
                      ? <><SpinnerGap className="mr-2 h-4 w-4 animate-spin" />Submitting&hellip;</>
                      : "Submit Booking Request"}
                  </Button>

                  <p className="text-[11px] text-center text-gray-400 pb-2">
                    We&apos;ll review your request within 2 business days.
                  </p>
                </div>
              </div>
            </form>
          </>
        );

        if (isMobile) {
          return (
            <Sheet open={sheetOpen} onOpenChange={(open) => { setSheetOpen(open); if (!open) setSubmitted(false); }}>
              <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto p-0 bg-gray-50">
                {formContent}
              </SheetContent>
            </Sheet>
          );
        }

        return (
          <Dialog open={sheetOpen} onOpenChange={(open) => { setSheetOpen(open); if (!open) setSubmitted(false); }}>
            <DialogContent aria-describedby={undefined} className="max-w-2xl p-0 overflow-hidden rounded-2xl bg-gray-50 border-0 max-h-[85vh] overflow-y-auto [&>button]:hidden">
              {formContent}
            </DialogContent>
          </Dialog>
        );
      })()}
    </div>
  );
}

